import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useCreateHeroSlide, useUpdateHeroSlide, HeroSlide } from "@/hooks/useHeroSlides";
import { ImageUploader } from "@/components/admin/ImageUploader";

interface HeroSlideFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slide?: HeroSlide;
}

export const HeroSlideFormDialog = ({ open, onOpenChange, slide }: HeroSlideFormDialogProps) => {
  const [formData, setFormData] = useState({
    title: slide?.title || "",
    subtitle: slide?.subtitle || "",
    description: slide?.description || "",
    cta_text: slide?.cta_text || "",
    cta_link: slide?.cta_link || "",
    image_url: slide?.image_url || "",
    sort_order: slide?.sort_order || 0,
    is_active: slide?.is_active ?? true,
  });

  const createMutation = useCreateHeroSlide();
  const updateMutation = useUpdateHeroSlide();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (slide) {
      await updateMutation.mutateAsync({ id: slide.id, ...formData });
    } else {
      await createMutation.mutateAsync(formData);
    }

    onOpenChange(false);
    setFormData({
      title: "",
      subtitle: "",
      description: "",
      cta_text: "",
      cta_link: "",
      image_url: "",
      sort_order: 0,
      is_active: true,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{slide ? "Edit Hero Slide" : "Add Hero Slide"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="subtitle">Subtitle</Label>
            <Input
              id="subtitle"
              value={formData.subtitle}
              onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <ImageUploader
            label="Image *"
            value={formData.image_url}
            onChange={(url) => setFormData({ ...formData, image_url: url })}
            folder="hero"
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cta_text">CTA Text</Label>
              <Input
                id="cta_text"
                value={formData.cta_text}
                onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                placeholder="Explore Tours"
              />
            </div>

            <div>
              <Label htmlFor="cta_link">CTA Link</Label>
              <Input
                id="cta_link"
                value={formData.cta_link}
                onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                placeholder="/tours"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="sort_order">Sort Order</Label>
            <Input
              id="sort_order"
              type="number"
              value={formData.sort_order}
              onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label htmlFor="is_active">Active</Label>
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {slide ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
